/**
 * Day ranges for the Statistics page. A day is a local calendar date kept as
 * "YYYY-MM-DD" (the user sits in Europe/Berlin, the browser's zone decides),
 * and a range is inclusive on both ends. Only when talking to the API is a
 * range turned into epoch ms: local midnight of the first day up to local
 * midnight of the day after the last day (exclusive).
 */

export type DayKey = string;

export interface DayRange {
  start: DayKey;
  end: DayKey;
}

export type CompareMode = 'prev' | 'month' | 'custom';

const pad2 = (n: number) => String(n).padStart(2, '0');

export function toKey(d: Date): DayKey {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parts(key: DayKey): [number, number, number] {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return [y, m - 1, d];
}

/** Local midnight of the day. */
export function fromKey(key: DayKey): Date {
  const [y, m, d] = parts(key);
  return new Date(y, m, d);
}

export function isValidKey(key: string | null | undefined): key is DayKey {
  if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(key)) return false;
  const [y, m, d] = parts(key);
  const date = new Date(y, m, d);
  return date.getFullYear() === y && date.getMonth() === m && date.getDate() === d;
}

export function todayKey(now: Date = new Date()): DayKey {
  return toKey(now);
}

export function addDays(key: DayKey, n: number): DayKey {
  const [y, m, d] = parts(key);
  return toKey(new Date(y, m, d + n));
}

/** Days in the range, both ends included. */
export function dayCount(range: DayRange): number {
  const [y1, m1, d1] = parts(range.start);
  const [y2, m2, d2] = parts(range.end);
  // UTC arithmetic so a DST change inside the range cannot cost an hour.
  return Math.round((Date.UTC(y2, m2, d2) - Date.UTC(y1, m1, d1)) / 86_400_000) + 1;
}

/** The same day `n` months away, clamped to the month's last day (31 Oct → 30 Sep). */
export function shiftMonths(key: DayKey, n: number): DayKey {
  const [y, m, d] = parts(key);
  const last = new Date(y, m + n + 1, 0).getDate();
  return toKey(new Date(y, m + n, Math.min(d, last)));
}

export function orderRange(a: DayKey, b: DayKey): DayRange {
  return a <= b ? { start: a, end: b } : { start: b, end: a };
}

/** The same number of days right before the range. */
export function previousPeriod(range: DayRange): DayRange {
  const n = dayCount(range);
  return { start: addDays(range.start, -n), end: addDays(range.start, -1) };
}

/** The same dates one month earlier (1–8 Oct → 1–8 Sep; 31 Mar → 28/29 Feb). */
export function sameDaysMonthBefore(range: DayRange): DayRange {
  return { start: shiftMonths(range.start, -1), end: shiftMonths(range.end, -1) };
}

export function compareRange(
  range: DayRange,
  mode: CompareMode,
  custom: DayRange | null,
): DayRange {
  if (mode === 'custom' && custom) return custom;
  if (mode === 'month') return sameDaysMonthBefore(range);
  return previousPeriod(range);
}

/** The API's period: `from` = local midnight of the first day, `to` = local midnight after the last day. */
export function rangeToPeriod(range: DayRange): { from: number; to: number } {
  return { from: fromKey(range.start).getTime(), to: fromKey(addDays(range.end, 1)).getTime() };
}

export function startOfMonth(key: DayKey): DayKey {
  return `${key.slice(0, 7)}-01`;
}

export function endOfMonth(key: DayKey): DayKey {
  const [y, m] = parts(key);
  return toKey(new Date(y, m + 1, 0));
}

/** The quick ranges in the period picker, in the order it lists them. */
export const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
] as const;

export type PresetId = (typeof PRESETS)[number]['id'];

/** "Last N days" include today; "Last month" is the whole calendar month before this one. */
export function presetRange(id: PresetId, today: DayKey): DayRange {
  const thisMonth = startOfMonth(today);
  switch (id) {
    case 'today':
      return { start: today, end: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { start: y, end: y };
    }
    case 'last7':
      return { start: addDays(today, -6), end: today };
    case 'last30':
      return { start: addDays(today, -29), end: today };
    case 'thisMonth':
      return { start: thisMonth, end: today };
    case 'lastMonth':
      return { start: shiftMonths(thisMonth, -1), end: addDays(thisMonth, -1) };
  }
}

export function sameRange(a: DayRange | null, b: DayRange | null): boolean {
  return !!a && !!b && a.start === b.start && a.end === b.end;
}

export function matchPreset(range: DayRange, today: DayKey): PresetId | null {
  return PRESETS.find((p) => sameRange(presetRange(p.id, today), range))?.id ?? null;
}

export function defaultRange(today: DayKey): DayRange {
  return presetRange('thisMonth', today);
}

const dayMonth = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const dayMonthYear = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const dayLong = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const monthYear = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });

export function formatDay(key: DayKey): string {
  return dayMonthYear.format(fromKey(key));
}

/** For a day button's aria-label: "Thursday 8 October 2026". */
export function formatDayLong(key: DayKey): string {
  return dayLong.format(fromKey(key));
}

export function formatMonth(year: number, month: number): string {
  return monthYear.format(new Date(year, month, 1));
}

/** "8 Oct 2026", "1 Oct – 8 Oct 2026" or "1 Dec 2025 – 8 Jan 2026". */
export function formatRange(range: DayRange): string {
  if (range.start === range.end) return formatDay(range.start);
  const sameYear = range.start.slice(0, 4) === range.end.slice(0, 4);
  const first = sameYear ? dayMonth.format(fromKey(range.start)) : formatDay(range.start);
  return `${first} – ${formatDay(range.end)}`;
}

/**
 * Short form for the picker's footer: "17 Aug – 23 Aug" or "1 Oct" when both
 * ends are in today's year, otherwise with the years ("30 Dec 2025 – 2 Jan 2026").
 */
export function formatRangeShort(range: DayRange, today: DayKey): string {
  const year = today.slice(0, 4);
  if (range.start.slice(0, 4) !== year || range.end.slice(0, 4) !== year) {
    return formatRange(range);
  }
  const first = dayMonth.format(fromKey(range.start));
  return range.start === range.end ? first : `${first} – ${dayMonth.format(fromKey(range.end))}`;
}

/** Months counted from year 0, so two months can be compared and stepped through. */
export function monthIndex(key: DayKey): number {
  const [y, m] = parts(key);
  return y * 12 + m;
}

export function monthFromIndex(index: number): { year: number; month: number } {
  return { year: Math.floor(index / 12), month: ((index % 12) + 12) % 12 };
}

/** A month as Monday-first weeks of seven cells, `null` before the 1st and after the last day. */
export function monthWeeks(year: number, month: number): (DayKey | null)[][] {
  const cells = monthGrid(year, month);
  while (cells.length % 7 !== 0) cells.push(null);
  return Array.from({ length: cells.length / 7 }, (_, i) => cells.slice(i * 7, i * 7 + 7));
}

export function monthGrid(year: number, month: number): (DayKey | null)[] {
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells: (DayKey | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= days; d++) cells.push(toKey(new Date(year, month, d)));
  return cells;
}

/** How far back the picker goes: this month and the 12 before it. */
export const CALENDAR_MONTHS = 13;

/** The first day the picker offers: the 1st of the month 12 months before this one. */
export function earliestDay(today: DayKey): DayKey {
  return shiftMonths(startOfMonth(today), -(CALENDAR_MONTHS - 1));
}
